<template>
    <vx-expandable-card color="info">
        <template v-slot:content>
            <div class="flex flex-col gap-6">
                <div>
                    <span class="font-extrabold text-2xl text-slate-700">Lifecycle Status Definitions</span>
                    <p class="text-sm text-slate-500 mb-0">
                        Configure how Intelligent Inbox treats lifecycle status and steers AI replies.
                    </p>
                </div>

                <page-loading v-if="loading"></page-loading>

                <vx-alert v-if="!loading && loadError" color="danger" :closeable="false" shadow>
                    <div class="flex items-center justify-between">
                        Failed to load lifecycle status definitions
                        <vx-button data-test="retry-btn" @click="fetchDefinitions" size="sm" color="danger">
                            Retry <font-awesome-icon widthAuto :icon="faRefresh" />
                        </vx-button>
                    </div>
                </vx-alert>

                <template v-if="!loading && !loadError">
                    <vx-alert v-if="definitions.length === 0" color="primary" :closeable="false">
                        Add your first lifecycle status definition.
                    </vx-alert>

                    <div v-if="definitions.length" class="flex flex-col gap-2">
                        <div ref="sortableEl" class="flex flex-col gap-2">
                            <div
                                v-for="(definition, index) in definitions"
                                :key="definition.id ?? definition.name + '-' + index"
                                class="lifecycle-status-draggable flex items-center"
                            >
                                <span
                                    class="lifecycle-status-handle pr-4 py-4"
                                    :class="syncing ? 'pointer-events-none opacity-50' : 'cursor-move'"
                                >
                                    <font-awesome-icon widthAuto :icon="faGripDotsVertical"></font-awesome-icon>
                                </span>
                                <lifecycle-status-definition-card
                                    class="grow"
                                    :definition="definition"
                                    :disabled="syncing"
                                    @edit="openEdit(index)"
                                    @delete="removeDefinition(index)"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="flex">
                        <vx-box class="p-4">
                            <vx-button
                                data-test="new-definition-btn"
                                v-if="definitions.length < MAX_DEFINITIONS"
                                size="lg"
                                :disabled="syncing"
                                @click="openCreate"
                            >
                                New Definition
                                <font-awesome-icon :icon="faCirclePlus"></font-awesome-icon>
                            </vx-button>
                        </vx-box>
                    </div>

                    <vx-alert v-if="definitions.length >= MAX_DEFINITIONS" color="info" :closeable="false">
                        Maximum of {{ MAX_DEFINITIONS }} lifecycle status definitions reached.
                    </vx-alert>
                </template>
            </div>
        </template>
    </vx-expandable-card>

    <modal-lifecycle-status-definition
        v-model:visible="modalVisible"
        :definition="modalDefinition"
        :editIndex="modalEditIndex"
        :syncing="syncing"
        @saved="onDefinitionSaved"
    />
</template>

<script lang="ts" setup>
import { faCirclePlus, faGripDotsVertical, faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBox, VxButton, VxExpandableCard, VxSwal } from '@voxie/frontend-components';
import { useSortable } from '@vueuse/integrations/useSortable';
import axios from 'axios';
import { nextTick, ref, watch } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useToasted } from '~/composables/useToasted';
import { lifecycleStatusDefinitionsClient } from '~/services';
import type {
    LifecycleStatusDefinitionFormData,
    LifecycleStatusDefinitionType,
} from '~/services/lifecycleStatusDefinitions/lifecycleStatusDefinitionsClient.types';
import { catchRequestError } from '~/utils/request';
import LifecycleStatusDefinitionCard from './LifecycleStatusDefinitionCard.vue';
import ModalLifecycleStatusDefinition from './ModalLifecycleStatusDefinition.vue';

const SAVE_CANCEL_KEY = Symbol('sync-lifecycle-status-definitions');

const MAX_DEFINITIONS = 20;
const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();

const loading = ref(false);
const loadError = ref(false);
const syncing = ref(false);
const definitions = ref<LifecycleStatusDefinitionType[]>([]);

const modalVisible = ref(false);
const modalDefinition = ref<LifecycleStatusDefinitionType | null>(null);
const modalEditIndex = ref<number | null>(null);

const openCreate = (): void => {
    modalDefinition.value = null;
    modalEditIndex.value = null;
    modalVisible.value = true;
};

const openEdit = (index: number): void => {
    modalDefinition.value = definitions.value[index] ?? null;
    modalEditIndex.value = index;
    modalVisible.value = true;
};

const syncDefinitions = async (formDataOverride?: LifecycleStatusDefinitionFormData[]): Promise<boolean> => {
    syncing.value = true;

    const formData: LifecycleStatusDefinitionFormData[] =
        formDataOverride ??
        definitions.value.map((definition) => ({
            name: definition.name,
            classification_steering: definition.classification_steering,
            message_steering: definition.message_steering,
        }));

    try {
        const response = await lifecycleStatusDefinitionsClient.updateLifecycleStatusDefinitions(
            { teamId },
            { definitions: formData },
            { cancelKey: SAVE_CANCEL_KEY }
        );
        definitions.value = response.data.definitions;
        return true;
    } catch (err) {
        if (axios.isCancel(err)) {
            return false;
        }
        toasted.error(catchRequestError(err));
        if (!formDataOverride) {
            await fetchDefinitions();
        }
        return false;
    } finally {
        syncing.value = false;
    }
};

const removeDefinition = async (index: number): Promise<void> => {
    const definition = definitions.value[index];

    VxSwal.confirmation(
        'Are you sure?',
        `Are you sure you want to delete "${definition?.name}"?`,
        'danger',
        async () => {
            const formData: LifecycleStatusDefinitionFormData[] = definitions.value
                .filter((_, i) => i !== index)
                .map((item) => ({
                    name: item.name,
                    classification_steering: item.classification_steering,
                    message_steering: item.message_steering,
                }));

            await syncDefinitions(formData);
        }
    );
};

const onDefinitionSaved = async (data: LifecycleStatusDefinitionFormData, editIndex: number | null): Promise<void> => {
    const formData: LifecycleStatusDefinitionFormData[] = definitions.value.map((definition) => ({
        name: definition.name,
        classification_steering: definition.classification_steering,
        message_steering: definition.message_steering,
    }));

    if (null !== editIndex && undefined !== formData[editIndex]) {
        formData[editIndex] = { ...data };
    } else {
        formData.push({ ...data });
    }

    const saved = await syncDefinitions(formData);
    if (saved) {
        modalVisible.value = false;
        toasted.success(
            null !== editIndex ? 'Lifecycle status definition updated' : 'Lifecycle status definition created'
        );
    }
};

const sortableEl = ref<HTMLElement | null>(null);

let sortableToastId: number | undefined;

const { start: startSortable } = useSortable(sortableEl, definitions, {
    handle: '.lifecycle-status-handle',
    draggable: '.lifecycle-status-draggable',
    animation: 200,
    onUpdate: () => {
        syncDefinitions().then((saved) => {
            if (saved) {
                if (sortableToastId) {
                    toasted.dismiss(sortableToastId);
                }

                sortableToastId = toasted.success('Lifecycle status definitions sorted and saved');
            }
        });
    },
} as Record<string, unknown>);

watch(loading, async (isLoading) => {
    if (!isLoading && !loadError.value) {
        await nextTick();
        startSortable();
    }
});

const fetchDefinitions = async (): Promise<void> => {
    loading.value = true;
    loadError.value = false;

    try {
        const response = await lifecycleStatusDefinitionsClient.getLifecycleStatusDefinitions({ teamId });
        definitions.value = response.data.definitions;
    } catch {
        loadError.value = true;
    } finally {
        loading.value = false;
    }
};

fetchDefinitions();
</script>
